import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import classNames from 'classnames';
import debounce from 'lodash/debounce.js';
import packageJson from '../../../package.json' with { type: 'json' };
import { isAuthError } from '../../companion-client/AuthError.js';
import { describeCompanionError } from '../../companion-client/errorCodes.js';
import ErrorWithCause from '../../utils/ErrorWithCause.js';
import { getErrorMessage, isAbortError, remoteFileObjToLocal, } from '../../utils/index.js';
import Browser from '../Browser.js';
import BulkActions from '../BulkActions.js';
import FilterInput from '../FilterInput.js';
import FooterActions from '../FooterActions.js';
import ItemDetailDialog from '../ItemDetailDialog.js';
import ProviderDialog from '../ProviderDialog.js';
import ProviderDialogController, {} from '../ProviderDialogController.js';
import addFiles from '../utils/addFiles.js';
import getClickedRange from '../utils/getClickedRange.js';
import handleError from '../utils/handleError.js';
import getBreadcrumbs from '../utils/PartialTreeUtils/getBreadcrumbs.js';
import getCheckedFilesWithPaths from '../utils/PartialTreeUtils/getCheckedFilesWithPaths.js';
import getNumberOfSelectedFiles from '../utils/PartialTreeUtils/getNumberOfSelectedFiles.js';
import PartialTreeUtils from '../utils/PartialTreeUtils/index.js';
import shouldHandleScroll from '../utils/shouldHandleScroll.js';
import AuthView from './AuthView.js';
import GlobalSearchView from './GlobalSearchView.js';
import Header from './Header.js';
export function defaultPickerIcon() {
    return (_jsx("svg", { "aria-hidden": "true", focusable: "false", width: "30", height: "30", viewBox: "0 0 30 30", children: _jsx("path", { d: "M15 30c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15C6.716 0 0 6.716 0 15c0 8.284 6.716 15 15 15zm4.258-12.676v6.846h-8.426v-6.846H5.204l9.82-12.364 9.82 12.364H19.26z" }) }));
}
const getDefaultState = (rootFolderId) => ({
    authenticated: undefined, // we don't know yet
    partialTree: [
        {
            type: 'root',
            id: rootFolderId,
            cached: false,
            nextPagePath: null,
        },
    ],
    currentFolderId: rootFolderId,
    searchString: '',
    didFirstRender: false,
    username: null,
    loading: false,
    selectionActive: false,
    detailItemId: undefined,
});
/** `node` and its ancestors below the root, nearest first. */
function lineage(byId, node) {
    const chain = [node];
    let parent = byId.get(node.parentId);
    while (parent && parent.type !== 'root') {
        chain.push(parent);
        parent = byId.get(parent.parentId);
    }
    return chain;
}
/**
 * Class to easily generate generic views for Provider plugins
 *
 * We have a *search view* and a *normal view*.
 * Search view is only used when the Provider supports server side search i.e. provider.search method is implemented for the provider.
 * The state is stored in searchResults.
 * Search view is implemented in components GlobalSearchView and SearchResultItem.
 * We conditionally switch between search view and normal in the render method when a server side search is initiated.
 * When users type their search query in search input box (SearchInput component), we debounce the input and call provider.search method to fetch results from the server.
 * when the user enters a folder in search results or clears the search input query we switch back to Normal View.
 */
export default class ProviderView {
    static VERSION = packageJson.version;
    // Test hook (mirrors GoldenRetriever pattern): allow tests to override debounce time
    // @ts-expect-error test-only hook key
    static [Symbol.for('uppy test: searchDebounceMs')];
    plugin;
    provider;
    opts;
    isHandlingScroll = false;
    previousCheckbox = null;
    #searchDebounced;
    constructor(plugin, opts) {
        this.plugin = plugin;
        this.provider = opts.provider;
        this.#dialogs = new ProviderDialogController(plugin);
        const defaultOptions = {
            viewType: 'list',
            showTitles: true,
            showFilter: true,
            showBreadcrumbs: true,
            loadAllFiles: false,
            virtualList: false,
        };
        this.opts = { ...defaultOptions, ...opts };
        this.openFolder = this.openFolder.bind(this);
        this.logout = this.logout.bind(this);
        this.handleAuth = this.handleAuth.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.resetPluginState = this.resetPluginState.bind(this);
        this.donePicking = this.donePicking.bind(this);
        this.render = this.render.bind(this);
        this.cancelSelection = this.cancelSelection.bind(this);
        this.toggleCheckbox = this.toggleCheckbox.bind(this);
        this.openSearchResultFolder = this.openSearchResultFolder.bind(this);
        this.clearSearchState = this.clearSearchState.bind(this);
        // Set default state for the plugin
        this.resetPluginState();
        // todo
        // @ts-expect-error this should be typed in @uppy/dashboard.
        this.plugin.uppy.on('dashboard:close-panel', this.resetPluginState);
        this.plugin.uppy.registerRequestClient(this.provider.provider, this.provider);
        // Configure debounced search with test override
        const testHookSymbol = Symbol.for('uppy test: searchDebounceMs');
        const testWait = ProviderView[testHookSymbol];
        const wait = testWait ?? 500;
        const debounceOpts = testWait === 0 ? { leading: true, trailing: true } : undefined;
        this.#searchDebounced = debounce(this.#search, wait, debounceOpts);
    }
    resetPluginState() {
        this.#selectionRoots.clear();
        this.#dialogs.cancel();
        this.plugin.setPluginState(getDefaultState(this.plugin.rootFolderId));
    }
    /**
     * Forget everything we know about the current folder and fetch it again.
     * Used after mutations (rename, delete, new folder, upload into folder).
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    refreshCurrentFolder = async (invalidateAll = false) => {
        const { partialTree, currentFolderId } = this.plugin.getPluginState();
        // A refresh scheduled after an action can land once the plugin is gone.
        if (partialTree == null)
            return;
        // Listing now would abort a running long operation, which refreshes the
        // folder itself when it ends.
        if (this.#cancelLongOperation)
            return;
        const refresh = ++this.#refreshes;
        // Remember what was selected so a refresh does not silently drop it.
        const checkedIds = partialTree.flatMap((node) => node.type !== 'root' &&
            node.parentId === currentFolderId &&
            node.status === 'checked'
            ? [node.id]
            : []);
        const byId = new Map(partialTree.map((node) => [node.id, node]));
        const isInsideCurrent = (node) => lineage(byId, node).some(({ parentId }) => parentId === currentFolderId);
        // A move can affect a previously visited destination, and a failed batch can have partial
        // writes. Retain navigation ancestors, but no sibling/descendant listings after a mutation.
        const navigationIds = new Set(getBreadcrumbs(partialTree, currentFolderId).map((node) => node.id));
        const nextTree = partialTree
            .filter((node) => node.type === 'root' || !isInsideCurrent(node))
            .filter((node) => !invalidateAll || navigationIds.has(node.id))
            .map((node) => (invalidateAll || node.id === currentFolderId) && node.type !== 'file'
            ? // Checked ancestors may only reflect previously selected children. Never let a new
                // listing inherit that aggregate selection; restore surviving children explicitly below.
                {
                    ...node,
                    status: 'unchecked',
                    cached: false,
                    nextPagePath: null,
                }
            : node);
        this.plugin.setPluginState({ partialTree: nextTree });
        await this.openFolder(currentFolderId);
        const { partialTree: refreshedTree, detailItemId } = this.plugin.getPluginState();
        // The plugin may have been removed while the folder was listed.
        if (refreshedTree == null)
            return;
        // The item of an open detail modal did not survive the refresh. Only
        // judged once the folder was listed again, and by the latest refresh (a
        // newer one aborted this listing and dropped the items again).
        const listed = refreshedTree.some((node) => node.id === currentFolderId && node.type !== 'file' && node.cached);
        if (refresh === this.#refreshes &&
            listed &&
            detailItemId &&
            !refreshedTree.some(({ id }) => id === detailItemId)) {
            this.closeItemDetail();
        }
        // Re-apply the selection to the items that survived the refresh.
        const survivors = checkedIds.filter((id) => refreshedTree.some((node) => node.type !== 'root' && node.id === id && node.status !== 'checked'));
        if (survivors.length > 0) {
            this.plugin.setPluginState({
                partialTree: PartialTreeUtils.afterToggleCheckbox(refreshedTree, survivors),
            });
        }
    };
    /**
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    runAction = (action, item) => this.#run(action, () => action.run({ item, ...this.#actionContext() }));
    /**
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    runToolbarAction = (action) => this.#run(action, () => action.run({
        currentFolderId: this.plugin.getPluginState().currentFolderId,
        ...this.#actionContext(),
    }));
    #actionContext = () => {
        const { uppy } = this.plugin;
        return { view: this, uppy, i18n: uppy.i18n };
    };
    /**
     * Runs an action and reports errors as toasts. An action that returns
     * `false` did nothing (a cancelled prompt) and keeps the current listing;
     * anything else, including a failure or a cancelled long operation, refreshes
     * the folder so a partially applied change shows.
     */
    async #run({ refresh }, run) {
        try {
            if ((await run()) === false)
                return;
        }
        catch (err) {
            this.#reportActionError(err);
        }
        if (refresh !== false)
            await this.refreshCurrentFolder(true);
    }
    #reportActionError(err) {
        const { uppy } = this.plugin;
        if (isAbortError(err)) {
            uppy.log('[ProviderView] action cancelled', 'warning');
            return;
        }
        uppy.log(`[ProviderView] action failed: ${getErrorMessage(err)}`, 'error');
        // A `UserFacingApiError` is for the user: a locale key from Companion, or a
        // translated message a plugin threw. Any other text is not: a failed
        // Companion request (the request client wraps those, auth errors aside)
        // says so, anything else (a bug, a browser API refusing) only that the
        // action failed.
        const message = err instanceof Error && err.name === 'UserFacingApiError'
            ? describeCompanionError(uppy.i18n, err)
            : err instanceof ErrorWithCause || isAuthError(err)
                ? uppy.i18n('companionError')
                : uppy.i18n('actionFailed');
        uppy.info(message, 'error', 5000);
    }
    #cancelLongOperation;
    /**
     * Runs a long write operation (a folder move, a bulk delete) behind the
     * loading screen, with a Cancel button and progress text. `signal` aborts
     * when the user cancels, the panel closes or uploads are cancelled; pass it
     * to every request. The operation should throw an `AbortError` when it
     * stops early, which `#run` treats as a cancel rather than a failure.
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    async runWithProgress(op) {
        try {
            await this.#withAbort(async (signal) => {
                // This operation's controller: a later request replaces the field.
                const controller = this.#abortController;
                this.#cancelLongOperation = () => controller?.abort();
                this.setLoading(true);
                await op({ signal, setProgress: (label) => this.setLoading(label) });
            });
        }
        finally {
            this.#cancelLongOperation = undefined;
            this.setLoading(false);
        }
    }
    /**
     * Manager mode: switch the multi-select checkboxes on or off.
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    toggleSelectionMode = () => {
        const { selectionActive } = this.plugin.getPluginState();
        if (selectionActive)
            this.cancelSelection();
        this.plugin.setPluginState({ selectionActive: !selectionActive });
    };
    /**
     * The item of the open detail modal as last seen in the tree. A refresh
     * drops the folder's items until it is listed again; the modal keeps
     * showing this one meanwhile instead of closing and reopening.
     */
    #detailItem;
    #refreshes = 0;
    /**
     * Manager mode: open the detail modal for one item.
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    openItemDetail = (item) => {
        this.#detailItem = item;
        this.plugin.setPluginState({ detailItemId: item.id });
    };
    /**
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    closeItemDetail = () => {
        this.plugin.setPluginState({ detailItemId: undefined });
    };
    // Checkbox aggregation also checks parents of selected children. Only user-selected folders
    // (and their selected descendants) may become folder mutation targets.
    #selectionRoots = new Set();
    /**
     * The items a bulk action gets: the checked ones, top-most only (an action
     * on a folder covers what is inside it). The selection count shown next to
     * the bulk actions counts these too.
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    getBulkActionItems = () => {
        const { partialTree } = this.plugin.getPluginState();
        const byId = new Map(partialTree.map((node) => [node.id, node]));
        const checked = partialTree.filter((node) => node.type !== 'root' &&
            node.status === 'checked' &&
            (node.type === 'file' ||
                lineage(byId, node).some(({ id }) => this.#selectionRoots.has(id))));
        const checkedIds = new Set(checked.map((node) => node.id));
        return checked.filter((node) => !lineage(byId, node)
            .slice(1)
            .some(({ id }) => checkedIds.has(id)));
    };
    /**
     * Run a bulk action over the checked items, then clear the selection.
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    runBulkAction = (action) => this.#run(action, async () => {
        const items = this.getBulkActionItems();
        if (items.length === 0)
            return false;
        const result = await action.run({ items, ...this.#actionContext() });
        if (result !== false)
            this.cancelSelection();
        return result;
    });
    #dialogs;
    /**
     * Ask the user for a string with an inline dialog (instead of `window.prompt`).
     * Resolves with `null` when the user cancels.
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    prompt(options) {
        return this.#dialogs.prompt(options);
    }
    /**
     * Ask the user to confirm something with an inline dialog (instead of `window.confirm`).
     *
     * @experimental Part of the file-management API added for `@uppy/s3`: it
     * will change incompatibly, also in minor releases.
     */
    confirm(options) {
        return this.#dialogs.confirm(options);
    }
    tearDown() {
        // Nothing.
    }
    setLoading(loading) {
        this.plugin.setPluginState({ loading });
    }
    get isLoading() {
        return this.plugin.getPluginState().loading;
    }
    cancelSelection() {
        this.#selectionRoots.clear();
        const { partialTree } = this.plugin.getPluginState();
        const newPartialTree = partialTree.map((item) => item.type === 'root' ? item : { ...item, status: 'unchecked' });
        this.plugin.setPluginState({ partialTree: newPartialTree });
    }
    clearSearchState() {
        this.plugin.setPluginState({
            searchResults: undefined,
        });
    }
    #abortController;
    async #withAbort(op) {
        // prevent multiple requests in parallel from causing race conditions
        this.#abortController?.abort();
        const abortController = new AbortController();
        this.#abortController = abortController;
        const cancelRequest = () => {
            abortController.abort();
        };
        try {
            // @ts-expect-error this should be typed in @uppy/dashboard.
            // Even then I don't think we can make this work without adding dashboard
            // as a dependency to provider-views.
            this.plugin.uppy.on('dashboard:close-panel', cancelRequest);
            this.plugin.uppy.on('cancel-all', cancelRequest);
            await op(abortController.signal);
        }
        finally {
            // Only this operation's controller: a newer operation may have replaced
            // it (and aborted this one) meanwhile.
            if (this.#abortController === abortController) {
                this.#abortController = undefined;
            }
            // @ts-expect-error this should be typed in @uppy/dashboard.
            // Even then I don't think we can make this work without adding dashboard
            // as a dependency to provider-views.
            this.plugin.uppy.off('dashboard:close-panel', cancelRequest);
            this.plugin.uppy.off('cancel-all', cancelRequest);
        }
    }
    /**
     * Ends a listing's loading state, unless a long operation (`runWithProgress`)
     * has taken the screen over meanwhile: the listing it aborted must not wipe
     * the progress screen on its way out.
     */
    #doneLoading() {
        if (this.#cancelLongOperation === undefined)
            this.setLoading(false);
    }
    async #search() {
        const { partialTree, currentFolderId, searchString } = this.plugin.getPluginState();
        const currentFolder = partialTree.find((i) => i.id === currentFolderId);
        if (searchString.trim() === '') {
            this.#abortController?.abort();
            this.clearSearchState();
            return;
        }
        this.setLoading(true);
        await this.#withAbort(async (signal) => {
            const scopePath = currentFolder.type === 'root' ? undefined : currentFolderId;
            const { items } = await this.provider.search(searchString, {
                signal,
                path: scopePath,
            });
            // For each searched file, build the entire path (from the root all the way to the leaf node)
            // This is because we need to make sure all ancestor folders are present in the partialTree before we open the folder or check the file.
            // This is needed because when the user opens a folder we need to have all its parent folders in the partialTree to be able to render the breadcrumbs correctly.
            // Similarly when the user checks a file, we need to have all it's ancestor folders in the partialTree to be able to percolateUp the checked state correctly to its ancestors.
            const { partialTree } = this.plugin.getPluginState();
            const newPartialTree = [...partialTree];
            for (const file of items) {
                // Decode URI and split into path segments
                const decodedPath = decodeURIComponent(file.requestPath);
                const segments = decodedPath.split('/').filter((s) => s.length > 0);
                // Start from root
                let parentId = this.plugin.rootFolderId;
                let isParentFolderChecked;
                // Walk through each segment starting from the root and build child nodes if they don't exist
                segments.forEach((segment, index, arr) => {
                    const pathSegments = segments.slice(0, index + 1);
                    const encodedPath = encodeURIComponent(`/${pathSegments.join('/')}`);
                    // Skip if node already exists
                    const existingNode = newPartialTree.find((n) => n.id === encodedPath && n.type !== 'root');
                    if (existingNode) {
                        parentId = encodedPath;
                        isParentFolderChecked = existingNode.status === 'checked';
                        return;
                    }
                    const isLeafNode = index === arr.length - 1;
                    let node;
                    // propagate checked state from parent to children, if the user has checked the parent folder before searching
                    // and the parent folder is an ancestor of the searched file
                    // see also afterOpenFolder which contains similar logic, we should probably refactor and reuse some
                    const status = isParentFolderChecked ? 'checked' : 'unchecked';
                    // Build the Leaf Node, it can be a file (`PartialTreeFile`) or a folder (`PartialTreeFolderNode`).
                    // Since we Already have the leaf node's data (`file`, `CompanionFile`) from the searchResults: CompanionFile[], we just use that.
                    if (isLeafNode) {
                        if (file.isFolder) {
                            node = {
                                type: 'folder',
                                id: encodedPath,
                                cached: false,
                                nextPagePath: null,
                                status,
                                parentId,
                                data: file,
                            };
                        }
                        else {
                            const restrictionError = this.validateSingleFile(file);
                            node = {
                                type: 'file',
                                id: encodedPath,
                                restrictionError,
                                status: !restrictionError ? status : 'unchecked',
                                parentId,
                                data: file,
                            };
                        }
                    }
                    else {
                        // not leaf node, so by definition it is a folder leading up to the leaf node
                        node = {
                            type: 'folder',
                            id: encodedPath,
                            cached: false,
                            nextPagePath: null,
                            status,
                            parentId,
                            data: {
                                // we don't have any data, so fill only the necessary fields
                                name: decodeURIComponent(segment),
                                icon: 'folder',
                                isFolder: true,
                            },
                        };
                    }
                    newPartialTree.push(node);
                    parentId = encodedPath; // This node becomes parent for the next iteration
                    isParentFolderChecked = status === 'checked';
                });
            }
            this.plugin.setPluginState({
                partialTree: newPartialTree,
                searchResults: items.map((item) => item.requestPath),
            });
        }).catch(handleError(this.plugin.uppy));
        this.#doneLoading();
    }
    // debounced search function is initialized in the constructor
    onSearchInput = (s) => {
        this.plugin.setPluginState({ searchString: s });
        if (this.opts.supportsSearch) {
            this.#searchDebounced();
        }
    };
    async openSearchResultFolder(folderId) {
        // stop searching
        this.plugin.setPluginState({ searchString: '' });
        // now open folder using the normal view
        await this.openFolder(folderId);
    }
    async openFolder(folderId) {
        // always switch away from the search view when opening a folder, whether it happens from the search view or by clicking breadcrumbs
        this.clearSearchState();
        this.previousCheckbox = null;
        // Returning cached folder
        const { partialTree } = this.plugin.getPluginState();
        const clickedFolder = partialTree.find((folder) => folder.id === folderId);
        if (clickedFolder.cached) {
            this.plugin.setPluginState({
                currentFolderId: folderId,
                searchString: '',
            });
            return;
        }
        this.setLoading(true);
        await this.#withAbort(async (signal) => {
            let currentPagePath = folderId;
            let currentItems = [];
            do {
                const { username, nextPagePath, items } = await this.provider.list(currentPagePath, {
                    signal,
                });
                // It's important to set the username during one of our first fetches
                this.plugin.setPluginState({ username });
                currentPagePath = nextPagePath;
                currentItems = currentItems.concat(items);
                this.setLoading(this.plugin.uppy.i18n('loadedXFiles', {
                    numFiles: currentItems.length,
                }));
            } while (this.opts.loadAllFiles && currentPagePath);
            const newPartialTree = PartialTreeUtils.afterOpenFolder(partialTree, currentItems, clickedFolder, currentPagePath, this.validateSingleFile);
            this.plugin.setPluginState({
                partialTree: newPartialTree,
                currentFolderId: folderId,
                searchString: '',
            });
        }).catch(handleError(this.plugin.uppy));
        this.#doneLoading();
    }
    /**
     * Removes session token on client side.
     */
    async logout() {
        this.#selectionRoots.clear();
        await this.#withAbort(async (signal) => {
            const res = await this.provider.logout({
                signal,
            });
            // res.ok is from the JSON body, not to be confused with Response.ok
            if (res.ok) {
                if (!res.revoked) {
                    const message = this.plugin.uppy.i18n('companionUnauthorizeHint', {
                        provider: this.plugin.title,
                        url: res.manual_revoke_url,
                    });
                    this.plugin.uppy.info(message, 'info', 7000);
                }
                this.plugin.setPluginState({
                    ...getDefaultState(this.plugin.rootFolderId),
                    authenticated: false,
                });
            }
        }).catch(handleError(this.plugin.uppy));
    }
    async handleAuth(authFormData) {
        await this.#withAbort(async (signal) => {
            this.setLoading(true);
            await this.provider.login({ authFormData, signal });
            this.plugin.setPluginState({ authenticated: true });
            await Promise.all([
                this.provider.fetchPreAuthToken(),
                this.openFolder(this.plugin.rootFolderId),
            ]);
        }).catch(handleError(this.plugin.uppy));
        this.#doneLoading();
    }
    async handleScroll(event) {
        if (shouldHandleScroll(event))
            await this.loadNextPage();
    }
    #nextPageRequest;
    /** Load the current folder's next page, shared by scrolling and programmatic navigation. */
    loadNextPage() {
        if (!this.#nextPageRequest) {
            this.#nextPageRequest = this.#loadNextPage().finally(() => {
                this.#nextPageRequest = undefined;
            });
        }
        return this.#nextPageRequest;
    }
    async #loadNextPage() {
        const { partialTree, currentFolderId } = this.plugin.getPluginState();
        const currentFolder = partialTree.find((i) => i.id === currentFolderId);
        if (currentFolder &&
            currentFolder.type !== 'file' &&
            currentFolder.nextPagePath) {
            const pagePath = currentFolder.nextPagePath;
            let loaded = false;
            this.isHandlingScroll = true;
            await this.#withAbort(async (signal) => {
                const { nextPagePath, items } = await this.provider.list(pagePath, { signal });
                const current = this.plugin.getPluginState();
                if (signal.aborted || current.currentFolderId !== currentFolderId)
                    return;
                const newPartialTree = PartialTreeUtils.afterScrollFolder(current.partialTree, currentFolderId, items, nextPagePath, this.validateSingleFile);
                this.plugin.setPluginState({ partialTree: newPartialTree });
                loaded = true;
            }).catch(handleError(this.plugin.uppy));
            this.isHandlingScroll = false;
            return loaded;
        }
        return false;
    }
    validateSingleFile = (file) => {
        const companionFile = remoteFileObjToLocal(file);
        const result = this.plugin.uppy.validateSingleFile(companionFile);
        return result;
    };
    async donePicking() {
        const { partialTree } = this.plugin.getPluginState();
        if (this.isLoading)
            return;
        this.setLoading(true);
        await this.#withAbort(async (signal) => {
            // 1. Enrich our partialTree by fetching all 'checked' but not-yet-fetched folders
            const enrichedTree = await PartialTreeUtils.afterFill(partialTree, (path) => this.provider.list(path, { signal }), this.validateSingleFile, (n) => {
                this.setLoading(this.plugin.uppy.i18n('addedNumFiles', { numFiles: n }));
            });
            // 2. Now that we know how many files there are - recheck aggregateRestrictions!
            const aggregateRestrictionError = this.validateAggregateRestrictions(enrichedTree);
            if (aggregateRestrictionError) {
                this.plugin.setPluginState({ partialTree: enrichedTree });
                return;
            }
            // 3. Add files
            const companionFiles = getCheckedFilesWithPaths(enrichedTree);
            addFiles(companionFiles, this.plugin, this.provider);
            // 4. Reset state
            this.resetPluginState();
        }).catch(handleError(this.plugin.uppy));
        this.#doneLoading();
    }
    toggleCheckbox(ourItem, isShiftKeyPressed) {
        const { partialTree } = this.plugin.getPluginState();
        const clickedRange = getClickedRange(ourItem.id, this.getDisplayedPartialTree(), isShiftKeyPressed, this.previousCheckbox);
        const newPartialTree = PartialTreeUtils.afterToggleCheckbox(partialTree, clickedRange);
        const statusById = new Map(newPartialTree.map((node) => [
            node.id,
            node.type === 'root' ? undefined : node.status,
        ]));
        for (const id of clickedRange) {
            if (statusById.get(id) === 'checked')
                this.#selectionRoots.add(id);
        }
        for (const id of this.#selectionRoots) {
            const status = statusById.get(id);
            if (status === undefined || status === 'unchecked')
                this.#selectionRoots.delete(id);
        }
        this.plugin.setPluginState({ partialTree: newPartialTree });
        this.previousCheckbox = ourItem.id;
    }
    getDisplayedPartialTree = () => {
        const { partialTree, currentFolderId, searchString } = this.plugin.getPluginState();
        const inThisFolder = partialTree.filter((item) => item.type !== 'root' && item.parentId === currentFolderId);
        // If provider supports server side search, we don't filter the items client side
        const filtered = this.opts.supportsSearch || searchString.trim() === ''
            ? inThisFolder
            : inThisFolder.filter((item) => (item.data.name ?? this.plugin.uppy.i18n('unnamed'))
                .toLowerCase()
                .indexOf(searchString.trim().toLowerCase()) !== -1);
        return filtered;
    };
    getBreadcrumbs = () => {
        const { partialTree, currentFolderId } = this.plugin.getPluginState();
        return getBreadcrumbs(partialTree, currentFolderId);
    };
    getSelectedAmount = () => {
        const { partialTree } = this.plugin.getPluginState();
        return getNumberOfSelectedFiles(partialTree);
    };
    validateAggregateRestrictions = (partialTree) => {
        const checkedFiles = partialTree.filter((item) => item.type === 'file' && item.status === 'checked');
        const uppyFiles = checkedFiles.map((file) => file.data);
        return this.plugin.uppy.validateAggregateRestrictions(uppyFiles);
    };
    #renderSearchResults() {
        const { i18n } = this.plugin.uppy;
        const { searchResults: ids, partialTree } = this.plugin.getPluginState();
        // todo memoize this so we don't have to do it on every render
        const itemsById = new Map();
        partialTree.forEach((item) => {
            if (item.type !== 'root') {
                itemsById.set(item.id, item);
            }
        });
        // the search results view needs data from the partial tree,
        const searchResults = ids.map((id) => {
            const partialTreeItem = itemsById.get(id);
            if (partialTreeItem == null)
                throw new Error('Partial tree not complete');
            return partialTreeItem;
        });
        return (_jsx(GlobalSearchView, { searchResults: searchResults, openFolder: this.openSearchResultFolder, toggleCheckbox: this.toggleCheckbox, i18n: i18n }));
    }
    render(_state, viewOptions = {}) {
        const { didFirstRender } = this.plugin.getPluginState();
        const { i18n } = this.plugin.uppy;
        if (!didFirstRender) {
            this.plugin.setPluginState({ didFirstRender: true });
            this.provider.fetchPreAuthToken();
            this.openFolder(this.plugin.rootFolderId);
        }
        const opts = { ...this.opts, ...viewOptions };
        const { authenticated, loading } = this.plugin.getPluginState();
        const pluginIcon = this.plugin.icon || defaultPickerIcon;
        if (authenticated === false) {
            return (_jsx(AuthView, { pluginName: this.plugin.title, pluginIcon: pluginIcon, handleAuth: this.handleAuth, i18n: this.plugin.uppy.i18n, renderForm: opts.renderAuthForm, loading: loading }));
        }
        const { partialTree, currentFolderId, username, searchString, searchResults, dialog, selectionActive = false, detailItemId, } = this.plugin.getPluginState();
        const breadcrumbs = this.getBreadcrumbs();
        const isManager = opts.mode === 'manager';
        const selectable = !isManager || selectionActive;
        // A long operation owns the screen: anything that would start another
        // request (and so abort it) is disabled or hidden meanwhile.
        const busy = this.#cancelLongOperation !== undefined;
        // Nothing to select in a folder we know is empty (unlike one being listed).
        const currentFolder = partialTree.find(({ id }) => id === currentFolderId);
        const isEmptyFolder = currentFolder?.type !== 'file' &&
            currentFolder?.cached === true &&
            !partialTree.some((node) => node.type !== 'root' && node.parentId === currentFolderId);
        if (detailItemId) {
            this.#detailItem =
                partialTree.find((node) => node.type !== 'root' && node.id === detailItemId) ?? this.#detailItem;
        }
        const detailItem = this.#detailItem?.id === detailItemId ? this.#detailItem : undefined;
        return (_jsxs("div", { className: classNames('uppy-ProviderBrowser', `uppy-ProviderBrowser-viewType--${opts.viewType}`), children: [_jsx(Header, { showBreadcrumbs: opts.showBreadcrumbs, openFolder: this.openFolder, breadcrumbs: breadcrumbs, pluginIcon: pluginIcon, title: this.plugin.title, logout: this.logout, username: username, i18n: i18n, toolbarActions: opts.toolbarActions ?? [], runToolbarAction: this.runToolbarAction, standalone: opts.standalone ?? false, selectionToggle: isManager &&
                        (opts.bulkActions?.length ?? 0) > 0 &&
                        (selectionActive || !isEmptyFolder)
                        ? {
                            active: selectionActive,
                            onToggle: this.toggleSelectionMode,
                        }
                        : undefined, busy: busy }), opts.showFilter && (_jsx(FilterInput, { value: searchString, onChange: (s) => this.onSearchInput(s), onSubmit: () => { }, inputLabel: i18n('filter'), i18n: i18n, disabled: busy })), searchResults ? (this.#renderSearchResults()) : (_jsx(Browser, { toggleCheckbox: this.toggleCheckbox, displayedPartialTree: this.getDisplayedPartialTree(), openFolder: this.openFolder, virtualList: opts.virtualList, noResultsLabel: i18n('noFilesFound'), handleScroll: this.handleScroll, viewType: opts.viewType, showTitles: opts.showTitles, i18n: this.plugin.uppy.i18n, isLoading: loading, onCancelLoading: this.#cancelLongOperation, utmSource: "Companion", actions: opts.actions ?? [], runAction: this.runAction, selectable: selectable, onFileClick: isManager && !selectionActive ? this.openItemDetail : undefined })), busy ? null : isManager ? (selectionActive && (_jsx(BulkActions, { selectedCount: this.getBulkActionItems().length, bulkActions: opts.bulkActions ?? [], runBulkAction: this.runBulkAction, i18n: i18n }))) : (_jsx(FooterActions, { partialTree: partialTree, donePicking: this.donePicking, cancelSelection: this.cancelSelection, i18n: i18n, validateAggregateRestrictions: this.validateAggregateRestrictions })), detailItem && (_jsx(ItemDetailDialog
                // A different item gets a fresh dialog (no stale preview).
                , { item: detailItem, actions: opts.actions ?? [], runAction: this.runAction, getPreviewUrl: opts.getPreviewUrl, onClose: this.closeItemDetail, i18n: i18n }, detailItem.id)), dialog && (_jsx(ProviderDialog, { dialog: dialog, i18n: i18n, onConfirm: this.#dialogs.submit, onCancel: this.#dialogs.cancel }, this.#dialogs.revision))] }));
    }
}
